import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  listDossierGraftEmails,
  listMyPendingEmails,
  graftPendingEmail,
  removeGraftedEmail,
  discardPendingEmail,
  moveGraftedEmail,
} from '../../services/dossiers/dossierEmailService';
import { SEMI_FRESH } from '../../lib/queryPresets';

const graftKeys = {
  dossier: (dossierId: string | undefined) => ['dossier-graft-emails', dossierId] as const,
  pending: () => ['pending-graft-emails'] as const,
};

export function useDossierGraftEmails(dossierId: string | undefined) {
  return useQuery({
    ...SEMI_FRESH,
    queryKey: graftKeys.dossier(dossierId),
    queryFn: () => listDossierGraftEmails(dossierId!),
    enabled: !!dossierId,
  });
}

export function usePendingGraftEmails() {
  return useQuery({
    ...SEMI_FRESH,
    queryKey: graftKeys.pending(),
    queryFn: listMyPendingEmails,
  });
}

function invalidateGraftQueries(qc: ReturnType<typeof useQueryClient>, dossierId: string | undefined) {
  qc.invalidateQueries({ queryKey: graftKeys.pending() });
  qc.invalidateQueries({ queryKey: ['dossier-graft-emails'] });
  qc.invalidateQueries({ queryKey: ['dossier-emails'] }); // gmailKeys.dossierEmails
  qc.invalidateQueries({ queryKey: ['dossier-tab-counts', dossierId] });
}

export function useGraftPendingEmail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ emailId, dossierId }: { emailId: string; dossierId: string }) =>
      graftPendingEmail(emailId, dossierId),
    onSuccess: (_data, { dossierId }) => invalidateGraftQueries(qc, dossierId),
  });
}

export function useRemoveGraftedEmail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ emailId }: { emailId: string; dossierId: string }) => removeGraftedEmail(emailId),
    onSuccess: (_data, { dossierId }) => invalidateGraftQueries(qc, dossierId),
  });
}

export function useMoveGraftedEmail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ emailId, targetDossierId }: { emailId: string; dossierId: string; targetDossierId: string }) =>
      moveGraftedEmail(emailId, targetDossierId),
    onSuccess: (_data, { dossierId, targetDossierId }) => {
      invalidateGraftQueries(qc, dossierId);
      qc.invalidateQueries({ queryKey: ['dossier-tab-counts', targetDossierId] });
    },
  });
}

export function useDiscardPendingEmail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (emailId: string) => discardPendingEmail(emailId),
    onSuccess: () => qc.invalidateQueries({ queryKey: graftKeys.pending() }),
  });
}
